import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material';
import { Link } from 'react-router-dom';
import moment from 'moment';

import { Receipt } from 'generated/graphql';

interface RecentReceiptsListProps {
  limit?: number;
  loading: boolean;
  receipts: Receipt[];
}

export const RecentReceiptsList: React.FC<RecentReceiptsListProps> = ({
  receipts = [],
  loading = true,
  limit = 10,
}) => {
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<keyof Receipt>('createdAt');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleRequestSort = (property: keyof Receipt) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedReceipts = [...receipts]
    .sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      if (order === 'desc') {
        return bValue < aValue ? -1 : 1;
      } else {
        return aValue < bValue ? -1 : 1;
      }
    })
    .slice(0, limit);

  const displayedReceipts = [...sortedReceipts].slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box component={Paper} style={{ padding: 35, marginTop: 20 }}>
      <Typography variant="h6" gutterBottom>
        Recent Receipts
      </Typography>
      {loading ? (
        <LinearProgress />
      ) : !loading && displayedReceipts.length ? (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Note</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('createdAt')}
                >
                  Created At
                </TableSortLabel>
              </TableCell>
              <TableCell>ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedReceipts.map((receipt) => {
              const receiptLink = `/donations/credit-seeding/${receipt.id}`;
              return (
                <TableRow key={receipt.id}>
                  <TableCell>
                    <Link to={receiptLink}>{receipt.data.status}</Link>
                  </TableCell>
                  <TableCell>
                    <Link to={receiptLink}>{receipt.metadata?.note}</Link>
                  </TableCell>
                  <TableCell>
                    <Link to={receiptLink}>{receipt.active ? 'Yes' : 'No'}</Link>
                  </TableCell>
                  <TableCell>
                    <Link to={receiptLink}>
                      {moment(receipt.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link to={receiptLink}>{receipt.id}</Link>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : null}
      {displayedReceipts?.length ? (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={sortedReceipts?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      ) : null}
    </Box>
  );
};
